import { RequestProps, User } from '../../../store/types'
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { useGetLightUsersQuery } from '../../../store/redux/reducers/usersSlice'
import { Dayjs } from 'dayjs'
import DateFormat from '../../../store/types/enums/DateFormat'
import { isEqual } from 'lodash'
import FilterMenu from '../FilterMenu'
import CollapsibleSection from '../../Button/CollapsibleSection'
import { t } from 'i18next'
import { Autocomplete, Box, Checkbox, ListItem, TextField } from '@mui/material'
import { costTypeOptions } from '../../Sidebar/cost/CostViewForm'
import { Option } from '../../Form/Inputs/SelectInput'
import Searchbar from '../../Searchbar/Searchbar'
import { useLeftPadding } from '../../../store/redux/hooks'
import ProjectSelector from '../../Form/Inputs/ProjectSelector'

type FilterCostSummaryProps = {
  requestProps: RequestProps
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
  setPage: Dispatch<SetStateAction<number>>
}

const FilterCostSummary = ({ requestProps, setRequestProps, setPage }: FilterCostSummaryProps) => {
  const leftPadding = useLeftPadding()
  const [payersPage, setPayersPage] = useState(0)
  const { data: payers } = useGetLightUsersQuery({
    page: payersPage,
    size: 50,
  })

  const [displayedSubmitters, setDisplayedSubmitters] = useState<User[]>([])
  const [selectedPayer, setSelectedPayer] = useState<User | null>(null)
  const [selectedTypes, setSelectedTypes] = useState<Option[]>([])
  const [selectedProject, setSelectedProject] = useState<Option | null>(null)

  useEffect(() => {
    if (payers) {
      setDisplayedSubmitters(prev => [...new Set([...prev, ...payers.content])])
    }
  }, [payers])

  const handleFetchMoreData = useCallback(
    (field: string) => {
      if (field === 'payers' && !payers?.last) {
        setPayersPage(prev => prev + 1)
      }
    },
    [payers]
  )

  const onSelectBottomScroll = useCallback(
    (event: React.SyntheticEvent, field: string) => {
      const listboxNode = event.currentTarget
      if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
        handleFetchMoreData(field)
      }
    },
    [handleFetchMoreData]
  )

  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(null)
  const [searchedPrincipal, setSearchedPrincipal] = useState<string | null>(null)

  const handleFiltering = () => {
    let newProps: RequestProps = { ...requestProps, page: 0 }

    newProps = {
      ...newProps,
      'author.id': selectedPayer !== null ? selectedPayer.id : undefined,
      startDate: selectedStartDate !== null ? selectedStartDate.format(DateFormat.YearMonthDay) : undefined,
      type: selectedTypes.length > 0 ? selectedTypes.map(type => type.value) : undefined,
      'project.id': selectedProject !== null ? selectedProject.value : undefined,
    }

    if (searchedPrincipal) {
      newProps = {
        ...newProps,
        principal: searchedPrincipal,
      }
    }

    if (isEqual(requestProps, newProps)) return
    setPage(0)
    setRequestProps({ ...newProps })
  }

  const handleClear = () => {
    setSelectedPayer(null)
    setSelectedStartDate(null)
    setSearchedPrincipal(null)
    setSelectedProject(null)
    setSelectedTypes([])
    setPage(0)
    setRequestProps({
      page: 0,
      size: requestProps.size,
    })
  }

  return (
    <FilterMenu filter={handleFiltering} clear={handleClear}>
      <CollapsibleSection text={t('page.costSummary.table.header.payer')}>
        <Autocomplete
          sx={{ pl: leftPadding }}
          value={selectedPayer}
          onChange={(_, value) => {
            if (value !== null) {
              setSelectedPayer(value)
            } else setSelectedPayer(null)
          }}
          getOptionLabel={user => `${user?.firstName} ${user?.lastName}`}
          options={displayedSubmitters}
          renderInput={params => {
            const { InputProps, inputProps, InputLabelProps, ...rest } = params
            return (
              <TextField
                InputProps={InputProps}
                inputProps={inputProps}
                {...rest}
                label={t('page.costSummary.selectPayer')}
                size="small"
              />
            )
          }}
          ListboxProps={{
            style: { maxHeight: 200 },
            onScroll: event => onSelectBottomScroll(event, 'payers'),
          }}
        />
      </CollapsibleSection>
      <CollapsibleSection text={t('page.costSummary.table.header.type')}>
        <Autocomplete
          sx={{ pl: leftPadding }}
          multiple
          disableCloseOnSelect
          limitTags={3}
          value={selectedTypes}
          onChange={(_, value) => {
            setSelectedTypes(value)
          }}
          options={costTypeOptions}
          getOptionLabel={option => t(option.label)}
          renderOption={(props, option, { selected }) => {
            const { key, ...otherProps } = props
            return (
              <ListItem key={key} {...otherProps} sx={{ gap: 1 }}>
                <Checkbox sx={{ ml: -1.5 }} checked={selected} />
                {t(option.label)}
              </ListItem>
            )
          }}
          renderInput={params => (
            <TextField
              size="small"
              fullWidth
              inputProps={params.inputProps}
              InputProps={params.InputProps}
              label={t('page.costSummary.selectType')}
            />
          )}
        />
      </CollapsibleSection>
      <CollapsibleSection text={t('page.costSummary.table.header.principal')}>
        <Box sx={{ pl: leftPadding }}>
          <Searchbar
            placeholder={t('page.costSummary.table.header.principal')}
            handleChange={value => {
              setSearchedPrincipal(value || null)
            }}
          ></Searchbar>
        </Box>
      </CollapsibleSection>
      <CollapsibleSection text={t('page.costSummary.table.header.project')}>
        <ProjectSelector
          value={selectedProject}
          onChange={setSelectedProject}
          label={t('page.costSummary.selectProject')}
          sx={{ pl: leftPadding }}
          loggable
        />
      </CollapsibleSection>
    </FilterMenu>
  )
}

export default FilterCostSummary
